import { FormControl, FormField } from '@/components/ui/Form';
import { Form, FormInput, FormItem, FormLabel, Switch } from '@/components/wm';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { isStateIndicator } from '../utils';
import { savedIndicatorSite, SiteIndicatorFields } from './SiteIndicatorFields';
import { Widget } from './Widget';

export function NewStateData({ data, onChangeField, onCompleted }) {
    const formSchema = z.object({
        indicatorId: z.string().min(1, i18next.t('form_validations.generic.indicator_required')),
        labelForOne: z.string().optional(),
        labelForZero: z.string().optional(),
        invertColors: z.boolean(),
        site: z.string().min(1, i18next.t('form_validations.generic.required'))
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            indicatorId: data.data?.indicatorId || '',
            labelForOne: data.data?.labelForOne || '',
            labelForZero: data.data?.labelForZero || '',
            // Inverted, 0 is the normal (green) state instead of 1.
            invertColors: data.data?.normalValue === 0,
            site: savedIndicatorSite(data.data?.indicatorId)
        },
        mode: 'onChange'
    });

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid]);

    const normalValue = data.data?.normalValue ?? 1;

    // Each label's dot shows the colour its value is drawn in, so the switch
    // below can be read off them.
    const labelField = (name, value) => (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem>
                    <FormLabel>
                        <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                            <span
                                className="tw-h-2.5 tw-w-2.5 tw-flex-shrink-0 tw-rounded-full tw-transition-colors"
                                style={{ backgroundColor: WiseMetering.SemaphorColors[value === normalValue ? 'green' : 'red'] }}
                            />
                            {i18next.t('dashboard.custom_dashboard.state_label_for', { value })}
                        </span>
                    </FormLabel>
                    <FormControl>
                        <FormInput
                            {...field}
                            onChange={e => {
                                field.onChange(e);
                                onChangeField('data')({ ...data.data, [name]: e.target.value });
                            }}
                            placeholder={i18next.t(value === 1 ? 'common.on' : 'common.off').capitalize()}
                        />
                    </FormControl>
                </FormItem>
            )}
        />
    );

    return (
        <div className="tw-flex tw-gap-6">
            <div className="tw-flex-1">
                <Form {...form}>
                    <SiteIndicatorFields
                        data={data}
                        emptyLabel={i18next.t('dashboard.custom_dashboard.no_state_indicators')}
                        filter={isStateIndicator}
                        form={form}
                        onChangeField={onChangeField}
                        onIndicatorChange={(_, indicatorId) => onChangeField('data')({ ...data.data, indicatorId })}
                    />
                    <div className="tw-flex tw-gap-4">
                        <div className="tw-flex-1">{labelField('labelForOne', 1)}</div>
                        <div className="tw-flex-1">{labelField('labelForZero', 0)}</div>
                    </div>
                    <FormField
                        control={form.control}
                        name="invertColors"
                        render={({ field }) => (
                            <FormItem>
                                <Switch
                                    checked={field.value}
                                    label={i18next.t('dashboard.custom_dashboard.state_invert_colors')}
                                    onCheckedChange={checked => {
                                        field.onChange(checked);
                                        onChangeField('data')({ ...data.data, normalValue: checked ? 0 : 1 });
                                    }}
                                />
                            </FormItem>
                        )}
                    />
                </Form>
            </div>
            <div className="tw-w-[320px] tw-h-[200px] tw-flex-shrink-0">
                <Widget widget={data}/>
            </div>
        </div>
    );
}
